/**
 * The summary column's wiring: five reads, one projection, and the seven flags that say which of
 * them have answered: five in `SummaryPending`, and two more for the two reads a card has to say
 * failed in words.
 *
 * It decides nothing. Every judgement — what a section says when its read failed, whether the goal
 * offers its two actions, how the attendance figures fold — is `model/summaryColumn`'s.
 *
 * ── Why the column's reads live HERE ──────────────────────────────────────────────────────────
 * Since KANON v3 the column stands on **Przegląd alone**, which makes it a surface like any tab
 * rather than a fixture of the screen. So its reads belong to the component that draws it, which is
 * what makes "only what is on screen fetches" true by construction rather than by a flag — an
 * unmounted column has no query. Held on the screen instead, as they were until 2 September 2026,
 * they cost three to four requests on every visit to the five tabs that draw none of them, and the
 * same again after every window refocus.
 *
 * The header read is repeated from `useClientDetail` and costs nothing: same options, same key, one
 * cache entry, one request.
 *
 * ── One `/consistency` window per tab, and the two windows are not the same quantity ──────────
 * Trendy reads `/consistency` too, over the range its own bar names, and until this hook moved the
 * two overlapped on that tab: two cache entries and two fetches of the same thirty-day answer, over
 * windows a day apart. They are **not** a duplicate to be unified. `attendanceRange` asks for
 * `ATTENDANCE_WINDOW_DAYS` CALENDAR DAYS ending today — thirty cells in a strip, so `from` is
 * `today − 29` — while `trendsRange` names a SPAN, `today − 30`, because `trendsSpanDays` is the
 * divisor the weekly volume rate is computed against and the backend rounds `(to − from).TotalDays`
 * to get it. Shortening the Trendy window to match the strip would divide every muscle-volume rate
 * by 29 instead of 30 and silently move every figure on that tab. Two questions, two windows; what
 * was wrong was asking both of them on one tab.
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { clientStanding } from '~/entities/client';

import {
  attendanceRange,
  clientComplianceQueryOptions,
  clientConsistencyQueryOptions,
  clientDetailQueryOptions,
  clientGoalQueryOptions,
  clientNoteQueryOptions,
} from '../api/clientDetailQueries';
import {
  toAttendanceDays,
  toDetailSource,
  toGoalSource,
  toNoteSource,
} from '../api/clientDetailSource';
import {
  buildSummaryColumn,
  isSelfPacedPlan,
  type SummaryColumnView,
} from '../model/summaryColumn';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { toIsoDate } from '~/shared/lib/calendarDate';

/**
 * Which of the column's reads are still in flight.
 *
 * `SummaryColumnView` says `null` for a card with nothing to draw, and `null` is three different
 * facts: the read has not answered, it failed, or it answered that there is nothing. The failures
 * already have flags below, because a card has to say "could not be loaded" in words. This is the
 * third one, and it exists for two reasons rather than one:
 *
 * · **The copy was wrong.** A card printed "Brak aktywnego celu" and "Brak zarejestrowanej
 *   objętości" while its own request was still open — an assertion about the client made before
 *   the answer arrived.
 * · **The height was wrong.** A one-line notice stands where a 172px card is about to, so the
 *   column re-flows under the cards below it the moment the response lands. Measured on the
 *   running screen on 1 September 2026: the goal card moved 191px and the plan card 76px, in one
 *   frame, about half a second after the tab opened. That is the "jump" the owner reported.
 *
 * One object rather than five more booleans on the state and five more props on the component: the
 * five move together, they are read in one place, and the component's interface stays small.
 */
export interface SummaryPending {
  readonly attendance: boolean;
  readonly plan: boolean;
  readonly goal: boolean;
  /**
   * The NOTE read. Added 2 September 2026 with the rest of the sweep: `NoteView.hasBody` is false
   * while `GET /customer/{id}/note` is still open, so the card printed "Brak notatki." about a
   * client whose note had not arrived — and offered `Edytuj` on a body it did not have.
   */
  readonly note: boolean;
  /**
   * The COMPLIANCE read, for the self-paced client's standing card.
   *
   * It had none, and the consequence was worse than a wrong word: the card's only branch was
   * `standing === null`, which is true while the read is open, when it fails, AND when it succeeds
   * with no active assignment — so a client in the third case read "Wczytuję klienta…" forever.
   */
  readonly standing: boolean;
}

export interface SummaryColumnState {
  readonly view: SummaryColumnView;
  /** Which cards are waiting on an answer, as opposed to having one that is empty. */
  readonly pending: SummaryPending;
  /**
   * The ATTENDANCE read failed, as opposed to being in flight.
   *
   * Surfaced separately because `view.attendance` is `null` for both, and the card has to tell them
   * apart to say the right thing. The screen used to derive it as `attendance === null && !loading`
   * — but `loading` was the HEADER's `isPending`, and the header read is quicker than
   * `/consistency` more often than not, so a card with a request still open printed "could not be
   * loaded".
   */
  readonly attendanceFailed: boolean;
  /**
   * The COMPLIANCE read failed, as opposed to being in flight — the standing card's own version of
   * the flag above, and separate for the same reason: `view.standing` is `null` for both.
   *
   * It never gates whether the standing card is shown. That is `view.selfPaced`, which comes from
   * the header read, so a failed compliance call costs the trainer the figures and says so — it
   * cannot fall the client back to an adherence percentage this mode has no honest version of.
   */
  readonly complianceFailed: boolean;
  /** The column's own today — the day the attendance window ends on, and the one it counts from. */
  readonly todayIso: string;
}

export function useSummaryColumn(clientId: number): SummaryColumnState {
  const { t, i18n } = useTranslation();
  const contentLang = useContentLanguage();
  // One clock for the visit: the window asked for and the days counted under it end on one today.
  const [now] = useState(() => new Date());

  const detail = useQuery(clientDetailQueryOptions(clientId));
  const note = useQuery(clientNoteQueryOptions(clientId));
  const consistency = useQuery(clientConsistencyQueryOptions(clientId, attendanceRange(now)));
  const goal = useQuery(clientGoalQueryOptions(clientId, contentLang));

  // The header read carries the plan's scheduling mode, and it is the read the whole screen is
  // gated on — so the mode is settled before any card draws, and the compliance call below is only
  // made for the clients whose card actually reads it.
  const detailSource = toDetailSource(detail.data);
  const compliance = useQuery(clientComplianceQueryOptions(clientId, detailSource));

  return {
    view: buildSummaryColumn(
      {
        detail: detailSource,
        note: toNoteSource(note.data),
        // A FAILED attendance read is `null`, exactly like one still in flight — the card says it
        // could not be loaded rather than drawing an empty strip, which would read as a client who
        // missed everything.
        consistency: consistency.isSuccess ? toAttendanceDays(consistency.data) : null,
        // Figures only. A failed or in-flight read leaves this `null` and the standing card says
        // so; it can no longer fall the client back to an adherence figure, because which card is
        // drawn is decided by the mode above and not by this.
        standing: clientStanding(compliance.data?.planStanding),
        goal: toGoalSource(goal.data),
      },
      { t, locale: i18n.language },
    ),
    pending: {
      attendance: consistency.isPending,
      // The plan rides on the HEADER read, which is also what decides whether the standing card or
      // the attendance card stands — so a column drawn before it answers knows neither.
      plan: detail.isPending,
      goal: goal.isPending,
      note: note.isPending,
      // A DISABLED query reports `isPending` for ever, because it will never fetch — so the flag
      // has to carry the enablement too, and it carries it through the SAME predicate the query
      // options use. `isFetching` would have been the tempting reading and it is wrong by one
      // render: the frame in which the header lands and says self-paced is a frame in which the
      // compliance query is enabled and has not started, and the card would have asserted an
      // answer in it.
      standing: isSelfPacedPlan(detailSource) && compliance.isPending,
    },
    attendanceFailed: consistency.isError,
    complianceFailed: compliance.isError,
    todayIso: toIsoDate(now),
  };
}
